import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useEffect, useRef, useState } from 'react';
// delay
import { useDispatch } from 'react-redux';
import { KEY_ADS_POSITION } from 'src/components/ads/dynamic-ads';
import Layout from 'src/components/layouts';
import { IS_NO_ROBOT_INDEX } from 'src/constants';
import { IMAGE_DEFAULT, WEB_DESC, WEB_KEYWORD, WEB_NAME, WEB_TITLE, WEB_URL } from 'src/constants/layout';
// import SectionMember from 'src/services/member-auth/components/section-member';
import { initialLists } from 'src/services/member-auth/redux';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import { isShouldFetch } from 'src/utils/detect-scrolling';
import { convertDatasets, convertObjPath, dropSomeField, escapeChar, isExistAds, timestamp } from 'src/utils/helper';
import { GET } from 'src/utils/services';
import useUser from 'src/utils/useUser';
import SectionOne from 'src/components/sections/sections-home/section-one';

// import Script from 'next/script';
// const SectionOne = dynamic(import('src/components/sections/sections-home/section-one'));
const SectionVideo = dynamic(import('src/components/sections/section-video'), { ssr: false });
const SectionTwo = dynamic(import('src/components/sections/sections-home/section-two'), { ssr: false });
// const SectionThaileague = dynamic(import('src/components/sections/section-event/section-thaileague'));
const SectionThree = dynamic(import('src/components/sections/sections-home/section-three'), { ssr: false });
const SectionFour = dynamic(import('src/components/sections/sections-home/section-four'), { ssr: false });
const SectionEvents = dynamic(import('src/components/sections/section-event/section-event-cover'), { ssr: false });
const SectionFive = dynamic(import('src/components/sections/sections-home/section-five'), { ssr: false });
const SectionLandingPage = dynamic(import('src/components/sections/section-event/section-landing-page'), { ssr: false });
const DynamicAds = dynamic(import('src/components/ads/dynamic-ads'));
const SectionMember = dynamic(() => import('src/services/member-auth/components/section-member'), { ssr: false });

const sectionsNeedShow = ['section-video', 'section-two', 'section-three', 'section-four', 'section-five'];

const PageIndex = ({ articles, covers, dataLandingPage, dataSectionVideo, ads, dataSetWorldCup, dataTagWorldCup }) => {
  const data = useUser();
  const [sessionSevId, setSessionSevId] = useState(null);
  const [sectionMember, setSectionMember] = useState({});
  useEffect(() => {
    const checkUser = async () => {
      if (!isEmpty(data) && !isEmpty(data?.user)) {
        const response = await fetch('/api/auth/metadata', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        if (response.ok) {
          const res = await response.json();
          localStorage.setItem('login-meta-data', res);
          setSectionMember(decryptionClient(res));
        }
      }
    };

    const loginMetaData = localStorage.getItem('login-meta-data');
    const decryptLoginMetaData = !isEmpty(loginMetaData) ? decryptionClient(loginMetaData) : {};
    const expireCondition = !isEmpty(loginMetaData) ? new Date(decryptLoginMetaData?.expire) < new Date() : true;

    if (!isEmpty(data) && !isEmpty(data?.user)) {
      setSessionSevId(decryptionClient(data?.user));
      isEmpty(loginMetaData) || expireCondition ? checkUser() : setSectionMember(decryptLoginMetaData);
    }
  }, [data]);
  const nextSectionIndexRef = useRef(0);
  const sectionsVisibleRef = useRef([]);
  const [sectionsVisible, setSectionsVisible] = useState([]);
  const sectionRef = useRef(0);
  const _article = {
    title: articles?.metaTag?.title || WEB_TITLE,
    keywords: articles?.metaTag?.keywords || WEB_KEYWORD,
    description: articles?.metaTag?.description || WEB_DESC
  };
  const handleScroll = async () => {
    if (isShouldFetch()) {
      document.removeEventListener('scroll', handleScroll);
      await loadMoreArticles();
    }
  };
  const loadMoreArticles = async () => {
    try {
      if (nextSectionIndexRef?.current < sectionsNeedShow.length) {
        if (isShouldFetch()) {
          const currentIndex = nextSectionIndexRef.current;
          const section = sectionsNeedShow[currentIndex];

          nextSectionIndexRef.current = currentIndex + 1;
          sectionsVisibleRef.current = [...sectionsVisibleRef.current, section];
          setSectionsVisible(sectionsVisibleRef.current);

          setTimeout(() => {
            loadMoreArticles();
          }, 1000);
        } else {
          document.addEventListener('scroll', handleScroll);
        }
      }
    } catch (err) {
      console.error(`${timestamp()} ==========> INDEX ERROR : ${nextSectionIndexRef.current}`, err);
    }
  };

  /* ----------------------- DATALAYER : SCROLL_TRACKING ---------------------- */
  useEffect(() => {
    const sectionsToCheck = ['section-1', 'section-2', 'section-3', 'section-4', 'section-5'];
    let ticking = false;

    const checkSections = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        const scrollTop = document.documentElement.scrollTop;

        for (const id of sectionsToCheck) {
          const el = document.getElementById(id);
          if (el && el.offsetTop < scrollTop) {
            sectionRef.current = id;
          }
        }

        // Footer check
        if (sectionRef.current === 'section-5' || sectionRef.current === 'section-4') {
          // logic check footer
        }

        ticking = false;
      });
    };

    document.addEventListener('scroll', checkSections, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('scroll', checkSections);
      document.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const articleDescriptionSeo = escapeChar(_article?.description) || WEB_DESC;
  return (
    <>
      {/* -------------------------------- NEXT_SEO -------------------------------- */}
      <NextSeo
        noindex={IS_NO_ROBOT_INDEX}
        nofollow={IS_NO_ROBOT_INDEX}
        title={_article.title}
        description={_article.description}
        robotsProps={{
          maxImagePreview: 'large',
          maxSnippet: -1,
          maxVideoPreview: -1
        }}
        openGraph={{
          type: 'article',
          locale: 'th_TH',
          url: WEB_URL.WEB,
          title: _article.title || WEB_TITLE,
          description: _article.description || WEB_DESC,
          images: [
            {
              url: IMAGE_DEFAULT,
              width: '1920',
              height: '1080',
              alt: WEB_NAME
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: _article.keywords || WEB_KEYWORD
          }
        ]}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={WEB_URL.WEB} />
        <meta property='twitter:title' content={_article?.title || WEB_TITLE} />
        <meta property='twitter:description' content={_article?.description || WEB_DESC} />
        <meta property='twitter:image' content={IMAGE_DEFAULT} />

        {/* --------------------------------- Truehit -------------------------------- */}
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
                __th_page="home";
              `
          }}
        />
        {/* ---------------------------- SNIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                  {
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    "name": "${WEB_NAME}",
                    "url": "${WEB_URL.WEB}",
                    "description": "${articleDescriptionSeo}",
                    "sameAs": [
                      "${WEB_URL.FACEBOOK}",
                      "${WEB_URL.YOUTUBE}",
                      "${WEB_URL.INSTAGRAM}"
                    ],
                    "potentialAction": {
                      "@type": "SearchAction",
                      "target": "${WEB_URL.WEB}search?q={search_term_string}",
                      "query-input": "required name=search_term_string"
                    }
                  }
                }
              `
          }}
        />

        {/* ---------------------------- SPIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                  {
                    "@context": "http://schema.org",
                    "@type": "WebPage",
                    "name": "${WEB_NAME}",
                    "description": "${articleDescriptionSeo}",
                    "publisher": {
                      "@type": "ProfilePage",
                      "name": "${WEB_NAME}"
                    }
                  }
                `
          }}
        />
        {/* -------------------------- Thaileague -------------------------- */}
        {/* {SECTION_THAILEAGUE === 'true' && <script async type='text/javascript' dangerouslySetInnerHTML={{
          __html: `
                  (function(a,d,e,b,f,c,s){a[b]=a[b]||function(){a[b].q.push(arguments);};a[b].q=[];c=d.createElement(e);c.async=1;c.src="//tdn.r42tag.com/lib/"+f+".js";s=d.getElementsByTagName(e)[0];s.parentNode.insertBefore(c,s);})(window,document,"script","_st", "1347-v1");

                  _st('setPageStructure', 'partnerwebsite|khobsanam');
                  _st('addTagProperties', {'site':'partnerwebsite','category':'khobsanam'});

                  _st("loadTags");
                  `
        }} />} */}
        {/* -------------------------------- ADS : PPN - AIS ------------------------------- */}
        {/* {SECTION_THAILEAGUE === 'true' && <script async type='text/javascript' dangerouslySetInnerHTML={{
          __html: `
            window.googletag = window.googletag || {cmd: []};
            googletag.cmd.push(function() {
              googletag.defineSlot('/231599934,12270527/khobsanam/AIS/AIS', [[300, 250]], 'div-gpt-ad-1631696625382-0').addService(googletag.pubads());
              googletag.pubads().enableSingleRequest();
              googletag.pubads().collapseEmptyDivs();
              googletag.enableServices();
            });
          `
        }} />} */}
      </Head>
      {/* ------------------------ First Screen Section ----------------------- */}
      <SectionLandingPage data={dataLandingPage} />

      {covers.length > 0 && <SectionEvents data={covers} />}
      <Layout sessionSevId={sessionSevId} ads={ads}>
        {/* <DynamicAds type={KEY_ADS_TYPE.BILLBOARD} section='desktop' adsName= 'billboard' /> */}
        {/* <MediaQuery> */}
        {/*  */}
        {/* ------------------------ Cover Event on top only ----------------------- */}

        {/* {SECTION_THAILEAGUE === 'true' && <SectionThaileague />} */}
        <SectionOne data={articles} ads={ads} dataSetWorldCup={dataSetWorldCup} dataTagWorldCup={dataTagWorldCup} />
        {/* ------------------------ Section Member ----------------------- */}
        {!isEmpty(sessionSevId) && !isEmpty(sectionMember) && (
          <SectionMember
            data={{
              user: dropSomeField(sectionMember, ['menu', 'tags', 'articlesTags', 'articlesCategories'])?.user,
              menu: sectionMember?.menu,
              tags: sectionMember?.tags,
              articlesTags: sectionMember?.articlesTags,
              articlesCategories: sectionMember?.articlesCategories
            }}
          />
        )}

        {sectionsVisible.includes('section-video') && <SectionVideo data={dataSectionVideo} />}
        {sectionsVisible.includes('section-two') && (
          <>
            <SectionTwo />
          </>
        )}
        {sectionsVisible.includes('section-three') && (
          <>
            {/* -------------------------------- ADS : PPN ------------------------------- */}
            {isExistAds(ads, KEY_ADS_POSITION.INARTICLE_3) && <DynamicAds position={KEY_ADS_POSITION.INARTICLE_3} data={ads} />}
            {/* --------------------------- ADS : INARTICLE - 3 -------------------------- */}
            <SectionThree />
          </>
        )}
        {sectionsVisible.includes('section-four') && (
          <>
            {/* --------------------------- ADS : INARTICLE - 4 -------------------------- */}
            {isExistAds(ads, KEY_ADS_POSITION.INARTICLE_4) && <DynamicAds position={KEY_ADS_POSITION.INARTICLE_4} data={ads} />}
            <SectionFour />
          </>
        )}
        {sectionsVisible.includes('section-five') && (
          <>
            {/* --------------------------- ADS : INARTICLE - 5 -------------------------- */}
            {isExistAds(ads, KEY_ADS_POSITION.INARTICLE_5) && <DynamicAds position={KEY_ADS_POSITION.INARTICLE_5} data={ads} />}
            <SectionFive />
            {/* --------------------------- ADS : INARTICLE - 6 -------------------------- */}
            {isExistAds(ads, KEY_ADS_POSITION.INARTICLE_6) && <DynamicAds position={KEY_ADS_POSITION.INARTICLE_6} data={ads} />}
          </>
        )}
      </Layout>
    </>
  );
};

export async function getServerSideProps(ctx) {
  const initProps = {
    statusCode: 200,
    errMessage: null,
    articles: {},
    tags: [],
    covers: [],
    dataLandingPage: [],
    dataSectionVideo: [],
    ads: [],
    dataSetWorldCup: null,
    dataTagWorldCup: [],
    session: null
  };

  const payload = {
    block1: 4,
    block2: 4,
    block3: 4,
    headerLatestNews: 10
  };

  try {
    const uri = `/api/v1.0/home/section-one${convertObjPath(payload)}`;

    // ✅ Parallel fetch ทุกตัวพร้อมกัน
    const [sectionData, coverData, landinPage, sectionVideo, dataSet] = await Promise.all([
      GET(uri),
      GET('/api/v1.0/imagecover'),
      GET('/api/landing-page'),
      GET('/api/v1.0/video/categories/video'),
      GET('/api/datasets')
    ]);

    // ✅ WorldCup — parallel กับ block อื่น ไม่ต้องรอ sequential
    let resWC = null;
    if (!isEmpty(dataSet)) {
      const data = find(dataSet, { topic: 'section-world-cup' })?.dataSet;
      if (!isEmpty(data)) {
        initProps.dataSetWorldCup = data;
        const tagName = convertDatasets(data, 'tag-name', true);
        if (tagName) {
          resWC = GET(`/api/search/tag${convertObjPath({ name: tagName })}`); // ไม่ await ก่อน
        }
      }
    }

    // ✅ resolve WorldCup พร้อมกับ assign ค่าอื่น
    const [wcResult] = await Promise.all([resWC ?? Promise.resolve(null)]);

    if (!isEmpty(wcResult)) {
      initProps.dataTagWorldCup = wcResult;
    }

    if (!isEmpty(sectionData)) {
      initProps.articles = sectionData;
      initProps.ads = sectionData?.ads;
      initProps.tags = sectionData.tags || [];
    }
    if (!isEmpty(coverData)) initProps.covers = coverData;
    if (!isEmpty(landinPage)) initProps.dataLandingPage = landinPage;
    if (!isEmpty(sectionVideo)) initProps.dataSectionVideo = sectionVideo;

    // ✅ Cache HTML — CDN เก็บ 60 วินาที, stale ได้ 5 นาที
    ctx.res.setHeader('Cache-Control', 'public, s-maxage=60, stale-while-revalidate=300');
  } catch (err) {
    console.error(`${timestamp()} ==========> PAGE_INDEX ERROR :`, err.message);
    initProps.errMessage = err.message;
    initProps.statusCode = err?.response?.status || 500;
  }

  return { props: initProps };
}

export default PageIndex;
